import { StyleFunctionProps } from '@chakra-ui/react';

export const Button = {
  defaultProps: {
    variant: 'primary',
    size: 'md',
  },
  baseStyle: {
    fontWeight: 'semibold',
    borderRadius: '8px',
    _disabled: {
      pointerEvents: 'none',
      backgroundColor: 'grey.offWhite',
      border: '2px',
      borderColor: 'grey.background',
    },
    _focus: {
      ringColor: 'white',
      boxShadow: 'focus',
    },
  },
  sizes: {
    xs: {
      borderRadius: '2px',
    },
    sm: {
      h: '32px',
      fontSize: 'sm',
      px: '16px',
      py: '6px',
    },
    md: {
      h: '40px',
      fontSize: 'md',
      px: '18px',
      py: '8px',
    },
    lg: {
      h: '52px',
      fontSize: 'lg',
      px: '24px',
      py: '12px',
    },
  },
  variants: {
    paymentSelectorGray: {
      h: '40px',
      px: '24px',
      borderRadius: '16px',
      fontSize: ['sm', 'md'],
      fontWeight: 'normal',
      backgroundColor: 'grey.offWhite',
      color: 'grey.primaryText',
      border: '1px solid',
      borderColor: 'grey.border',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      _hover: {
        backgroundColor: 'blue.50',
        _dark: {
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
        },
      },
      _active: {
        backgroundColor: 'blue.200',
        _dark: {
          backgroundColor: 'blue.500',
        },
      },
      _focus: {
        ring: 0,
        outline: 'none',
        tabIndex: -1,
      },
    },

    success: {
      backgroundColor: 'success.button',
      color: 'grey.white',
      _hover: {
        backgroundColor: 'success.text',
        _disabled: {
          backgroundColor: 'success.button.disabled',
        },
      },
      _disabled: {
        backgroundColor: 'success.button.disabled',
      },
      _focus: {
        ring: 0,
        outline: 'none',
        tabIndex: -1,
      },
    },
    primary: {
      backgroundColor: 'primary.button',
      color: 'grey.white',
      _hover: {
        backgroundColor: 'primary.button.hover',
      },
      _disabled: {
        backgroundColor: 'primary.button.disabled',
      },
      _focus: {
        ring: 0,
        outline: 'none',
        tabIndex: -1,
      },
    },
    grey: {
      backgroundColor: 'grey.border',
      color: 'grey.secondaryText',
      _hover: {
        backgroundColor: 'grey.offWhite',
        outline: 'none',
        boxShadow: 'none',
      },
      _focus: {
        ring: 0,
        outline: 'none',
        tabIndex: -1,
      },
    },
    primaryGhost: (styleProps: StyleFunctionProps) => {
      const hoverBackgroundValue =
        styleProps.colorMode === 'light' ? 'primary.background' : 'grey.border';
      return {
        backgroundColor: 'none',
        color: 'primary.button',
        _hover: {
          backgroundColor: hoverBackgroundValue,
        },
        _disabled: {
          backgroundColor: 'none',
          border: '0',
          color: 'grey.tertiaryText',
        },
        _focus: {
          ring: 0,
          outline: 'none',
          tabIndex: -1,
        },
        _active: {
          backgroundColor: 'primary.background',
          color: 'primary.text',
        },
      };
    },
    secondaryGhost: {
      fontWeight: 'medium',
      fontSize: '14px',
      backgroundColor: 'none',
      color: 'grey.tertiaryText',
      _hover: {
        color: 'primary.text',
        backgroundColor: 'primary.background',
      },
      _disabled: {
        backgroundColor: 'none',
        border: '0',
        color: 'grey.tertiaryText',
      },
      _active: {
        backgroundColor: 'primary.background',
        color: 'primary.text',
      },
      _focus: {
        ring: 0,
        outline: 'none',
        tabIndex: -1,
      },
    },
    error: {
      backgroundColor: 'error.button',
      boxShadow: 'none',
      color: 'error.text',
      _hover: {
        backgroundColor: 'red.200',
        _dark: {
          backgroundColor: 'red.300',
        },
      },
      _disabled: {
        backgroundColor: 'none',
        color: 'grey.border',
      },
      _focus: {
        ring: 0,
        outline: 'none',
        tabIndex: -1,
      },
    },
    errorGhost: {
      backgroundColor: 'none',
      color: 'red.600',
      _hover: {
        backgroundColor: 'red.50',
      },
      _disabled: {
        backgroundColor: 'none',
        color: 'grey.border',
      },
      _focus: {
        ring: 0,
        outline: 'none',
        tabIndex: -1,
      },
    },
    grayGhost: {
      backgroundColor: 'none',
      color: 'grey.tertiaryText',
      _hover: {
        backgroundColor: 'grey.background',
        color: 'grey.secondaryText',
        svg: {
          stroke: 'grey.secondaryText',
        },
      },
      _disabled: {
        backgroundColor: 'none',
        color: 'grey.tertiaryText',
      },
    },
    grayOutline: {
      border: '1px solid',
      borderColor: 'grey.border',
      backgroundColor: 'none',
      color: 'grey.secondaryText',
      _hover: {
        backgroundColor: 'grey.background',
      },
      _disabled: {
        backgroundColor: 'none',
        color: 'grey.tertiaryText',
      },
    },
    onboarding: {
      backgroundColor: 'green.400',
      color: 'grey.white',
      _hover: {
        backgroundColor: 'green.700',
      },
      _focus: {
        ring: 0,
        outline: 'none',
        tabIndex: -1,
      },
    },
    lightBlue: {
      backgroundColor: 'blue.700',
      color: 'white',
      _hover: {
        backgroundColor: 'blue.600',
      },
      _focus: {
        ring: 0,
        outline: 'none',
        tabIndex: -1,
      },
    },
    lightBlueGhost: (styleProps: StyleFunctionProps) => {
      return {
        backgroundColor:
          styleProps.colorMode === 'light' ? 'blue.100' : 'blue.700',
        color: styleProps.colorMode === 'light' ? 'blue.700' : 'blue.100',
      };
    },
  },
};
